import { useCallback, useMemo, useState } from 'react';
import { ProjectMetaForm } from './components/ProjectMetaForm';
import { GanttGrid, type CellRange } from './components/GanttGrid';
import { ItemPicker } from './components/ItemPicker';
import { TsvOutput } from './components/TsvOutput';
import { DraftsBar } from './components/DraftsBar';
import { ThemeToggle } from './components/ThemeToggle';
import { useCategories } from './hooks/useCategories';
import { useDrafts } from './hooks/useDrafts';
import type { GanttData, ProjectMeta } from './types';
import { addDays, dateRange } from './lib/dates';
import { GROUP_NAMES, itemsForGroup } from './config/categories';

// 依目前網域決定入口：新主機（*.mrturnkey.com.tw）回新 portal，Zeabur 回舊 portal（2026-10 搬家並行期）
const PORTAL_HOME = (window.location.hostname.endsWith('mrturnkey.com.tw') ? 'https://portal.mrturnkey.com.tw/' : 'https://tb-portal.zeabur.app/');

export default function App() {
  const cats = useCategories();
  const drafts = useDrafts();
  const cur = drafts.current;

  const [pickerRange, setPickerRange] = useState<CellRange | null>(null);

  const groups = GROUP_NAMES;
  const pickerCatalog = useMemo(
    () => pickerRange ? itemsForGroup(pickerRange.group) : [],
    [pickerRange]
  );

  const setMeta = useCallback((meta: ProjectMeta) => {
    drafts.updateCurrent({ meta });
  }, [drafts]);

  const setGantt = useCallback((gantt: GanttData) => {
    drafts.updateCurrent({ gantt });
  }, [drafts]);

  const effectiveStart = useMemo(
    () => cur.gridStart || (cur.meta.startDate ? addDays(cur.meta.startDate, -3) : ''),
    [cur.gridStart, cur.meta.startDate]
  );
  const effectiveEnd = useMemo(
    () => cur.gridEnd || (cur.meta.endDate ? addDays(cur.meta.endDate, 3) : ''),
    [cur.gridEnd, cur.meta.endDate]
  );

  const onApplyPicker = useCallback((newItems: string[]) => {
    if (!pickerRange) return;
    const { group, startDate, endDate } = pickerRange;
    const dates = dateRange(startDate, endDate);
    const next: GanttData = { ...cur.gantt };
    const row = { ...(next[group] ?? {}) };
    for (const d of dates) {
      if (newItems.length === 0) {
        delete row[d];
      } else {
        row[d] = [...newItems];
      }
    }
    next[group] = row;
    setGantt(next);
    setPickerRange(null);
  }, [pickerRange, cur.gantt, setGantt]);

  const onClearPicker = useCallback(() => {
    if (!pickerRange) return;
    const { group, startDate, endDate } = pickerRange;
    const dates = dateRange(startDate, endDate);
    const next: GanttData = { ...cur.gantt };
    const row = { ...(next[group] ?? {}) };
    for (const d of dates) delete row[d];
    next[group] = row;
    setGantt(next);
    setPickerRange(null);
  }, [pickerRange, cur.gantt, setGantt]);

  return (
    <div className="min-h-screen p-4 md:p-6 max-w-[1600px] mx-auto space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-x-4 gap-y-3 pb-3 border-b border-tb-border">
        <div className="flex items-end gap-4">
          <span
            className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-brand-yellow text-brand-black font-black text-lg shrink-0 self-center"
            style={{ boxShadow: '0 2px 8px rgba(249,185,27,.25)' }}
            title="統包先生 Mr. Turnkey"
            aria-hidden
          >統</span>
          <div>
            <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-tb-text3 mb-1">
              TB · GANTT TOOL
            </div>
            <h1
              className="text-2xl md:text-3xl text-tb-text leading-tight m-0"
              style={{ fontFamily: "'Archivo Black','Taipei Sans TC Beta',sans-serif", letterSpacing: '-0.02em' }}
            >
              工程 <span className="text-tb-accent-ink">甘特排程</span>
            </h1>
            <p className="text-xs text-tb-text2 mt-1">
              點擊或拖拉甘特圖網格填入工項，再一鍵複製貼回「工作管理表 / 2026」分頁。
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <ThemeToggle />
          <a
            href={PORTAL_HOME}
            className="text-xs text-tb-text2 hover:text-tb-text hover:underline whitespace-nowrap"
          >⬅ TB Portal</a>
          <a
            href="https://docs.google.com/spreadsheets/d/1opyfWp6KtDmTtjoXTU0QtBi9BsP4xlOs5uYeR41zspA/edit"
            target="_blank"
            rel="noreferrer"
            className="text-xs font-bold text-tb-accent-ink hover:text-tb-text border border-brand-yellow/40 px-3 py-1.5 rounded whitespace-nowrap transition-colors"
          >開啟工作管理表 ↗</a>
        </div>
      </header>

      <DraftsBar
        drafts={drafts.drafts}
        currentId={drafts.currentId}
        onSwitch={drafts.switchDraft}
        onCreate={() => drafts.createDraft('新案件')}
        onDelete={drafts.deleteDraft}
        onRename={drafts.renameDraft}
      />

      <ProjectMetaForm value={cur.meta} onChange={setMeta} />

      <div className="rounded-lg border border-tb-border bg-tb-surface p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-tb-text mb-3">甘特圖顯示範圍</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="起始日（含 buffer）">
            <input type="date" className="input" value={effectiveStart} onChange={e => drafts.updateCurrent({ gridStart: e.target.value })} />
          </Field>
          <Field label="結束日（含 buffer）">
            <input type="date" className="input" value={effectiveEnd} onChange={e => drafts.updateCurrent({ gridEnd: e.target.value })} />
          </Field>
          <div className="col-span-2 flex items-end text-xs text-tb-text2">
            預設取「開工日 −3」到「預計完工日 +3」；可手動覆寫。
          </div>
        </div>
      </div>

      <section>
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="text-sm font-semibold text-tb-text">
            甘特圖網格
            {cats.loading && <span className="ml-2 text-xs text-tb-text3">載入分類中…</span>}
            {cats.error && <span className="ml-2 text-xs text-tb-rust">分類載入失敗：{cats.error}</span>}
          </h2>
          <div className="text-xs text-tb-text2 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-tb-moss-bg border border-tb-moss-border" /> 已填工項</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-tb-surface2 border border-tb-border" /> 週末</span>
            <span className="inline-flex items-center gap-1"><span className="w-3 h-3 rounded-sm bg-tb-rust-bg border border-tb-rust-border" /> 國定假日</span>
          </div>
        </div>
        <GanttGrid
          startDate={effectiveStart}
          endDate={effectiveEnd}
          groups={groups}
          data={cur.gantt}
          holidaySet={cats.holidaySet}
          onRangePick={setPickerRange}
        />
        <p className="mt-2 text-xs text-tb-text3">
          ※ 點擊一格 = 編輯該日；按住拖拉 = 一次填整段日期（同列內）。
        </p>
      </section>

      <TsvOutput
        meta={cur.meta}
        gantt={cur.gantt}
        groups={groups}
        tsvStart={cur.tsvStart}
        tsvEnd={cur.tsvEnd}
        onTsvStartChange={v => drafts.updateCurrent({ tsvStart: v })}
        onTsvEndChange={v => drafts.updateCurrent({ tsvEnd: v })}
      />

      <ItemPicker
        open={!!pickerRange}
        group={pickerRange?.group ?? null}
        startDate={pickerRange?.startDate ?? ''}
        endDate={pickerRange?.endDate ?? ''}
        initialItems={pickerRange?.initialItems ?? []}
        catalog={pickerCatalog}
        anchor={pickerRange?.anchor ?? null}
        onClose={() => setPickerRange(null)}
        onApply={onApplyPicker}
        onClear={onClearPicker}
      />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-tb-text2">{label}</span>
      {children}
    </label>
  );
}
